/**
 * AsyncScreen.tsx — 7-state async UI shell
 *
 * Purpose:    Single component that renders one of 7 mutually-exclusive UI states
 *             for every ɳSentry dashboard surface. Eliminates per-dashboard boilerplate.
 * Inputs:     AsyncScreenProps (state, typed renders per state)
 * Outputs:    Correct UI for the current state
 * Constraints: No external deps; React 19; works inside any parent container
 * States:     idle | loading | empty | error | offline | permission-denied | rate-limited | success
 * SOT:        T-P3-E5-W2-S4-T01
 */

import React from 'react'
import type { SentryError } from '../lib/sentry-error'

export type AsyncState =
  | 'idle'
  | 'loading'
  | 'empty'
  | 'error'
  | 'offline'
  | 'permission-denied'
  | 'rate-limited'
  | 'success'

export interface AsyncScreenProps {
  state: AsyncState
  /** Rendered when state === 'success' */
  children: React.ReactNode
  /** Custom loading skeleton; defaults to generic pulse */
  loadingSkeleton?: React.ReactNode
  /** Message shown in the empty state */
  emptyMessage?: string
  /** CTA shown below emptyMessage */
  emptyAction?: React.ReactNode
  /** The typed error (required when state === 'error' | 'rate-limited') */
  error?: SentryError
  /** Retry callback shown in error and offline states */
  onRetry?: () => void
}

// ---------------------------------------------------------------------------
// Sub-state components
// ---------------------------------------------------------------------------

function LoadingState({ skeleton }: { skeleton?: React.ReactNode }) {
  if (skeleton) return <>{skeleton}</>
  return (
    <div className="animate-pulse space-y-4 p-6" aria-label="Loading…" role="status">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="h-10 rounded-md bg-brand-surface-2" />
      ))}
    </div>
  )
}

function EmptyState({
  message,
  action,
}: {
  message?: string
  action?: React.ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
      <svg
        aria-hidden="true"
        className="h-12 w-12 text-brand-muted"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z"
        />
      </svg>
      <p className="text-lg font-medium text-brand-secondary">
        {message ?? 'No data yet'}
      </p>
      {action && <div>{action}</div>}
    </div>
  )
}

function ErrorCard({
  error,
  onRetry,
}: {
  error?: SentryError
  onRetry?: () => void
}) {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-950/40 p-6 text-center">
      <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">
        Error
      </p>
      <p className="mb-4 text-brand-secondary">
        {error?.message ?? 'Something went wrong. Please try again.'}
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          Retry
        </button>
      )}
    </div>
  )
}

function OfflineState({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-yellow-200 dark:border-yellow-800/60 bg-yellow-50 dark:bg-yellow-950/40 p-6 text-center">
      <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-yellow-700 dark:text-yellow-400">
        Plugin offline
      </p>
      <p className="mb-4 text-brand-secondary">
        Plugin offline — check nself status
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-yellow-700 px-4 py-2 text-sm font-medium text-brand-ink hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500"
        >
          Retry
        </button>
      )}
    </div>
  )
}

function PermissionDeniedState() {
  return (
    <div className="mx-auto max-w-md rounded-xl border border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 p-6 text-center">
      <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-400">
        Bundle required
      </p>
      <p className="mb-4 text-brand-secondary">
        ɳSentry bundle required — upgrade to access this dashboard
      </p>
      <a
        href="/pricing"
        className="inline-block rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
      >
        View pricing
      </a>
    </div>
  )
}

function RateLimitedState({
  error,
  onRetry,
}: {
  error?: SentryError
  onRetry?: () => void
}) {
  const seconds = error?.retryAfterSeconds ?? 60
  return (
    <div className="mx-auto max-w-md rounded-xl border border-orange-200 dark:border-orange-800/60 bg-orange-50 dark:bg-orange-950/40 p-6 text-center">
      <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-orange-700 dark:text-orange-400">
        Rate limited
      </p>
      <p className="mb-4 text-brand-secondary">
        Too many requests — please wait {seconds}s before retrying
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-md bg-orange-700 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          Retry
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function AsyncScreen({
  state,
  children,
  loadingSkeleton,
  emptyMessage,
  emptyAction,
  error,
  onRetry,
}: AsyncScreenProps) {
  switch (state) {
    case 'idle':
      return null
    case 'loading':
      return <LoadingState skeleton={loadingSkeleton} />
    case 'empty':
      return <EmptyState message={emptyMessage} action={emptyAction} />
    case 'error':
      return <ErrorCard error={error} onRetry={onRetry} />
    case 'offline':
      return <OfflineState onRetry={onRetry} />
    case 'permission-denied':
      return <PermissionDeniedState />
    case 'rate-limited':
      return <RateLimitedState error={error} onRetry={onRetry} />
    case 'success':
      return <>{children}</>
  }
}
